import apiClient from "./apiClient";
import { PRODUCTS_PAGE_SIZE } from "./productsConstants";
import { getFilteredProducts } from "./ProductsFilter";

export { PRODUCTS_PAGE_SIZE };

const toArray = (data) => {
  if (!data) return [];
  return Array.isArray(data) ? data : [data];
};

/** Lazy store access — avoids circular import with AppRoutes/lazy pages. */
const getStore = () => require("../index").default;

const dispatchProductPage = (products) => {
  const store = getStore();
  const list = toArray(products);
  store.dispatch({ type: "GET_PRODUCTS", products: list });
  store.dispatch({
    type: "GET_ACTIVE_PRODUCTS",
    active_products: getFilteredProducts(list, "active"),
  });
  store.dispatch({
    type: "GET_ARCHIVED_PRODUCTS",
    archived_products: getFilteredProducts(list, "archived"),
  });
  store.dispatch({
    type: "GET_DRAFT_PRODUCTS",
    draft_products: getFilteredProducts(list, "draft"),
  });
  return list;
};

/** Fetch one page of products — does not crawl further pages. */
export const fetchProductsPage = async ({
  start = 0,
  size = PRODUCTS_PAGE_SIZE,
  status = "All",
  context = "Loading Products",
  silent = false,
} = {}) => {
  const params = new URLSearchParams();
  params.set("start", String(start));
  params.set("size", String(size));
  if (status) params.set("status", status);
  const data = await apiClient.get(`/products?${params.toString()}`, {
    context,
    silent,
    hideLoader: silent,
  });
  return toArray(data);
};

/**
 * Load the first products page into Redux (replace, do not accumulate all pages).
 */
export const refreshProductsInStore = async () => {
  const products = await fetchProductsPage({
    start: 0,
    size: PRODUCTS_PAGE_SIZE,
    status: "All",
    context: "Refreshing Products",
    silent: true,
  });
  return dispatchProductPage(products);
};

const findProductInStore = (id) => {
  const s = String(id ?? "").trim();
  if (!s || s.toLowerCase() === "null") return null;
  const store = getStore();
  const state = store.getState()?.productsState || {};
  const pools = [
    state.products,
    state.active_products,
    state.draft_products,
    state.archived_products,
  ];
  for (const pool of pools) {
    const list = Array.isArray(pool) ? pool : [];
    const hit = list.find((p) => String(p?.id || p?.POID || p?.poid) === s);
    if (hit) return hit;
  }
  return null;
};

/** True when product id is already in any Redux products slice. */
export const isProductInStore = (id) => Boolean(findProductInStore(id));

/** Resolve ids to store hits vs ids that still need GET /products/{id}. */
export const partitionProductIds = (ids = []) => {
  const loaded = new Map();
  const missing = [];
  for (const raw of ids) {
    const s = String(raw ?? "").trim();
    if (!s || s.toLowerCase() === "null" || s.toLowerCase() === "undefined") continue;
    const hit = findProductInStore(s);
    if (hit) loaded.set(s, hit);
    else missing.push(s);
  }
  return { loaded, missing: [...new Set(missing)] };
};

/** GET /products/{id} — only when not already in Redux; upserts on fetch. */
export const fetchProductById = async (id, { silent = true, context = "Loading Product" } = {}) => {
  const s = String(id ?? "").trim();
  if (!s || s.toLowerCase() === "null" || s.toLowerCase() === "undefined") return null;

  const cached = findProductInStore(s);
  if (cached) return cached;

  try {
    const data = await apiClient.get(`/products/${encodeURIComponent(s)}`, {
      context,
      silent,
      hideLoader: silent,
    });
    const product = Array.isArray(data) ? data[0] : data;
    if (product && typeof product === "object") {
      upsertProductInStore(product);
      return product;
    }
  } catch (e) {
    console.warn(`fetchProductById(${s}) failed:`, e?.message || e);
  }
  return null;
};

/**
 * Resolve products by id — uses Redux when loaded; GET /products/{id} only for missing ids.
 * Returns Map id → product.
 */
export const fetchProductsByIds = async (ids = [], opts = {}) => {
  const { loaded, missing } = partitionProductIds(ids);
  const map = new Map(loaded);
  if (!missing.length) return map;

  const { batchSize = 5 } = opts;
  for (let i = 0; i < missing.length; i += batchSize) {
    const chunk = missing.slice(i, i + batchSize);
    await Promise.all(
      chunk.map(async (id) => {
        const product = await fetchProductById(id, opts);
        if (product) map.set(id, product);
      })
    );
  }
  return map;
};

/**
 * Patch / insert one product in Redux after a successful API response / search hit.
 * Keeps Active/Draft slices in sync so Products list tabs show search hits beyond page 1.
 */
export const upsertProductInStore = (product) => {
  if (!product) return;
  const store = getStore();
  const normalized = {
    ...product,
    POID: product.POID || product.id || product.poid,
    id: product.id || product.POID || product.poid,
    status: product.status || product.lifeCycleStatus || "Active",
  };
  if (!normalized.POID) return;

  store.dispatch({ type: "POST_PRODUCTS", products: normalized });

  const status = String(normalized.status || "").toLowerCase();
  if (status === "active") {
    store.dispatch({ type: "POST_ACTIVE_PRODUCTS", active_product: normalized });
  } else if (status === "draft") {
    store.dispatch({ type: "POST_DRAFT_PRODUCTS", draft_product: normalized });
  } else if (status === "archived") {
    store.dispatch({ type: "POST_ARCHIVED_PRODUCTS", archived_products: normalized });
  }
};
